import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Download, Check, Undo2 } from 'lucide-react';
import { request, api } from '../api/client';
import { Drawer, Table, Skeleton, Result, Button, Input, Modal, useApp } from '../ui';
import { Pill } from './TwDataTable';
import { formatIst } from '../lib/time';
import { usePermissionChecker } from '../store/auth';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_PILL: Record<string, string> = { POSTED: 'green', DRAFT: 'gold', CANCELLED: 'red' };
const LEDGER_PILL: Record<string, string> = { CREDIT_NOTE: 'green', DEBIT_NOTE: 'red' };

/**
 * The canonical credit / debit-note view — a single drawer every surface deep-links to (the retailer ledger,
 * order detail, invoice detail, return detail, credit-note register). It resolves every link the note carries
 * (retailer, invoice, order, return), shows the reversal audit when the note was voided, and the ledger
 * movements it produced (the CREDIT_NOTE credit that granted the advance, and any compensating DEBIT_NOTE that
 * removed it on a reversal) — so a note is traceable from anywhere. `id` null → closed.
 *
 * For a TURNOVER_BONUS note (a standalone financial incentive, no return/invoice), it also carries the
 * maker-checker actions: approve a DRAFT → post it, or reverse a POSTED one. `onChanged` lets the opener (the
 * register) refetch after either. Both actions are permission-gated.
 */
export function CreditNoteDrawer({ id, onClose, onChanged }: { id: string | null; onClose: () => void; onChanged?: () => void }) {
  const { message } = useApp();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');

  const { data: n, isLoading, isError } = useQuery({
    queryKey: ['credit-note', id],
    queryFn: () => request<any>({ url: `/credit-notes/${id}` }),
    enabled: !!id,
  });

  const isCredit = n?.noteType === 'CREDIT';
  const cancelled = n?.status === 'CANCELLED';
  const isBonus = n?.source === 'TURNOVER_BONUS';

  const refresh = () => { qc.invalidateQueries({ queryKey: ['credit-note', id] }); onChanged?.(); };
  const approve = useMutation({
    mutationFn: () => api.post(`/credit-notes/${id}/approve-bonus`),
    onSuccess: () => { message.success('Bonus approved & posted to the retailer ledger'); refresh(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not approve the bonus'),
  });
  const reverse = useMutation({
    mutationFn: () => api.post(`/credit-notes/${id}/reverse-bonus`, { reason: reverseReason.trim() }),
    onSuccess: () => { message.success('Bonus reversed'); setReverseOpen(false); setReverseReason(''); refresh(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not reverse the bonus'),
  });

  const openPdf = async () => {
    try {
      const r = await api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch {
      message.error('Failed to load the note PDF');
    }
  };

  return (
    <Drawer
      open={!!id}
      onClose={onClose}
      width={480}
      title={
        <span className="inline-flex items-center gap-2">
          <FileText size={16} />
          {n ? `${isCredit ? 'Credit' : 'Debit'} Note ${n.noteNo}` : 'Note'}
        </span>
      }
    >
      {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {isError && <Result status="404" title="Note not found" subTitle="It may have been removed, or you may not have access." />}
      {n && (
        <div className="space-y-4">
          {/* Header: status + headline amount */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-2xl font-semibold text-content">{inr2(n.amount)}</div>
              <div className="text-xs text-subtle">{n.noteDate ? String(n.noteDate).substring(0, 10) : '—'}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Pill color={STATUS_PILL[n.status] ?? 'default'}>{cancelled ? 'Reversed' : String(n.status ?? '').toLowerCase()}</Pill>
              {isBonus && <Pill color="blue">Turnover bonus</Pill>}
            </div>
          </div>

          {/* Draft bonus banner — not yet posted to the ledger; awaiting a checker's approval. */}
          {isBonus && n.status === 'DRAFT' && (
            <div className="rounded-control border border-warning-500/25 bg-warning-500/10 px-3 py-2 text-xs text-content">
              This turnover bonus is a <span className="font-semibold">draft</span> — it has not yet been posted to the retailer's ledger. Approving it credits their account (advance-in-credit).
            </div>
          )}

          {/* Reversal banner — a CANCELLED note was voided in error-correction; keep the trail visible. */}
          {cancelled && (
            <div className="rounded-control border border-danger-600/25 bg-danger-600/10 px-3 py-2 text-xs text-content">
              This {isBonus ? 'bonus' : 'credit note'} was <span className="font-semibold">reversed</span>
              {n.reversedAt ? ` on ${formatIst(n.reversedAt)}` : ''}{n.reversedByName ? ` by ${n.reversedByName}` : ''}.
              {n.reversalReason ? <div className="mt-0.5 text-muted">Reason: {n.reversalReason}</div> : null}
              <div className="mt-0.5 text-muted">The advance-in-credit it granted was removed by a compensating debit; {isBonus ? 'a corrected bonus can be re-issued.' : 'a corrected note can be re-issued from the return.'}</div>
            </div>
          )}

          {/* Tax split */}
          <div className="rounded-control border border-line bg-surface-2 px-3 py-2 text-[13px]">
            <Row k={isBonus ? 'Incentive value' : 'Taxable value'} v={inr2(n.taxableAmt)} />
            {Number(n.cgstAmt) > 0 && <Row k="CGST" v={inr2(n.cgstAmt)} />}
            {Number(n.sgstAmt) > 0 && <Row k="SGST" v={inr2(n.sgstAmt)} />}
            {Number(n.igstAmt) > 0 && <Row k="IGST" v={inr2(n.igstAmt)} />}
            {isBonus && <Row k="GST" v="Not applicable (financial credit)" />}
            <div className="mt-1 border-t border-line pt-1"><Row k="Total" v={inr2(n.amount)} bold /></div>
          </div>

          {/* Linked documents — trace to everywhere this note connects. */}
          <div className="rounded-control border border-line bg-surface px-3 py-2 text-[13px]">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">Linked to</div>
            <LinkRow k="Retailer" text={n.retailerName ? `${n.retailerCode ? n.retailerCode + ' · ' : ''}${n.retailerName}` : `#${n.retailerId}`} />
            {isBonus && <Row k="Kind" v={`Turnover / volume bonus${n.financialYear ? ` · FY ${n.financialYear}` : ''}`} />}
            {n.returnId && <LinkRow k="Sales return" text={n.returnNo ?? `#${n.returnId}`} to={`/returns/${n.returnId}`} onNav={onClose} />}
            {n.invoiceId && <LinkRow k="Against invoice" text={n.invoiceNo ?? `#${n.invoiceId}`} to={`/billing/invoices/${n.invoiceId}`} onNav={onClose} />}
            {n.orderId && <LinkRow k="Order" text={n.orderNo ?? `#${n.orderId}`} to={`/orders/${n.orderId}`} onNav={onClose} />}
            {n.reason && <Row k="Reason" v={n.reason} />}
            {n.createdByName && <Row k="Issued by" v={`${n.createdByName}${n.createdAt ? ` · ${formatIst(n.createdAt)}` : ''}`} />}
          </div>

          {/* Ledger trail: the credit granted + any reversal debit. */}
          {(n.ledger?.length ?? 0) > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">Ledger movements</div>
              <Table
                size="small"
                pagination={false}
                rowKey={(l: any) => String(l.id)}
                dataSource={n.ledger}
                columns={[
                  { title: 'When', dataIndex: 'createdAt', render: (v: any) => <span className="text-xs">{formatIst(v)}</span> },
                  { title: 'Type', dataIndex: 'txnType', render: (v: string) => <Pill color={LEDGER_PILL[v] ?? 'default'}>{String(v ?? '').replace(/_/g, ' ').toLowerCase()}</Pill> },
                  { title: 'Debit', dataIndex: 'debit', align: 'right' as const, render: (v: any) => Number(v) > 0 ? <span className="text-danger-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
                  { title: 'Credit', dataIndex: 'credit', align: 'right' as const, render: (v: any) => Number(v) > 0 ? <span className="text-success-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
                ]}
              />
            </div>
          )}

          {/* Turnover-bonus maker-checker actions (permission-gated). */}
          {isBonus && n.status === 'DRAFT' && hasPerm('CREDIT_NOTE.BONUS.APPROVE') && (
            <Button variant="primary" icon={<Check size={16} />} loading={approve.isPending} onClick={() => approve.mutate()} block>
              Approve &amp; post {inr2(n.amount)}
            </Button>
          )}
          {isBonus && n.status === 'POSTED' && hasPerm('CREDIT_NOTE.BONUS.REVERSE') && (
            <Button variant="danger" icon={<Undo2 size={16} />} onClick={() => setReverseOpen(true)} block>
              Reverse bonus
            </Button>
          )}

          {isCredit && (
            <button onClick={openPdf} className="inline-flex items-center gap-2 rounded-control border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content hover:bg-surface-3">
              <Download size={16} /> Download credit note PDF
            </button>
          )}

          {/* Reverse-reason capture — a compensating debit is posted; the note stays for audit. */}
          <Modal
            open={reverseOpen}
            title="Reverse turnover bonus"
            onOpenChange={(o) => { if (!o) { setReverseOpen(false); setReverseReason(''); } }}
            footer={
              <>
                <Button variant="default" onClick={() => { setReverseOpen(false); setReverseReason(''); }}>Cancel</Button>
                <Button variant="danger" disabled={!reverseReason.trim()} loading={reverse.isPending} onClick={() => reverse.mutate()}>Reverse</Button>
              </>
            }
          >
            <p className="mb-2 text-sm text-muted">
              This posts a compensating debit that removes the advance-in-credit this bonus granted, and marks the
              note reversed (kept for audit). This can't be undone — a corrected bonus can be raised afresh.
            </p>
            <Input.TextArea rows={3} placeholder="Reason for reversal…" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
          </Modal>
        </div>
      )}
    </Drawer>
  );
}

function Row({ k, v, bold }: { k: string; v: any; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-muted">{k}</span>
      <span className={bold ? 'font-semibold text-content' : 'text-content'}>{v}</span>
    </div>
  );
}

function LinkRow({ k, text, to, onNav }: { k: string; text: string; to?: string; onNav?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-muted">{k}</span>
      {to
        ? <Link to={to} onClick={onNav} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{text}</Link>
        : <span className="text-content">{text}</span>}
    </div>
  );
}
